import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { MaterialModule } from '@app/material.module';
import { DevicesApiService } from '@services/api/devices/devices-api.service';
import { TechnicianApiService } from '@services/api/user/technician/technician-api.service';
import { CompanyFilterService } from '@services/company-filter/company-filter.service';
import { DEVICE_TYPES, DEVICE_USAGES, Device } from '@interfaces/device';
import { Company } from '@interfaces/company';
import { Technician } from '@interfaces/technician';
import { Roles } from '@enums/Roles';

/** add = a device added by us (trusted, enabled at once); edit = change one; request = the backoffice asks us for one */
export type DeviceFormMode = 'add' | 'edit' | 'request';
export interface DeviceFormData {
  device: Device | null;
  mode: DeviceFormMode;
}

/**
 * Add, edit or ask for a device. Saves by itself and closes with the mode only once the server accepted it, so a
 * rejected save (e.g. serial number already used) keeps what was typed and shows why.
 */
@Component({
  selector: 'app-device-form',
  imports: [MaterialModule, ReactiveFormsModule, TranslateModule],
  standalone: true,
  templateUrl: './device-form.component.html',
  styleUrl: './device-form.component.scss'
})
export class DeviceFormComponent implements OnInit {
  private dialogRef: MatDialogRef<DeviceFormComponent, DeviceFormMode> = inject(MatDialogRef);
  private devices_api_service = inject(DevicesApiService);
  private technician_api_service = inject(TechnicianApiService);
  private companyFilter = inject(CompanyFilterService);
  private translate = inject(TranslateService);
  private data: DeviceFormData = inject(MAT_DIALOG_DATA);
  protected device: Device | null = this.data?.device ?? null;
  protected mode: DeviceFormMode = this.data?.mode ?? (this.device ? 'edit' : 'add');
  protected readonly types = DEVICE_TYPES;
  protected readonly usages = DEVICE_USAGES;
  protected companies: Company[] = [];
  protected superadmin = false;
  private technicians: Technician[] = [];
  protected saving = false;
  protected errorMessage = '';

  form = new FormGroup({
    name: new FormControl(this.device?.name ?? '', { nonNullable: true, validators: [Validators.required] }),
    serial_number: new FormControl(this.device?.serial_number ?? '', { nonNullable: true, validators: [Validators.required] }),
    device_type: new FormControl<string>(this.device?.device_type ?? '', { nonNullable: true }),
    usage: new FormControl<string>(this.device?.usage ?? '', { nonNullable: true }),
    company: new FormControl<number | null>(this.device?.company ?? null),
    requested_for: new FormControl<number | null>(this.device?.requested_for ?? null),
    brand: new FormControl(this.device?.brand ?? '', { nonNullable: true }),
    model: new FormControl(this.device?.model ?? '', { nonNullable: true }),
    description: new FormControl(this.device?.description ?? '', { nonNullable: true }),
  });

  async ngOnInit(): Promise<void> {
    // Every device says what it is for: usage, and the type unless it is "other" (always a phone). The API refuses
    // an approved device without them (03/11), so they are asked here too, not only in a request
    this.form.controls.usage.addValidators(Validators.required);
    this.form.controls.device_type.addValidators(Validators.required);
    this.form.controls.usage.valueChanges.subscribe(() => this.applyUsage());
    this.form.controls.company.valueChanges.subscribe(() => this.dropTechnicianOfOtherCompany());
    this.applyUsage();

    await this.companyFilter.load();
    this.superadmin = this.companyFilter.isSuperadmin;
    if (this.superadmin) {
      this.companies = await firstValueFrom(this.companyFilter.companies$);
      if (!this.form.controls.company.value) {
        // A new device goes to the company chosen in the "Azienda" menu, or to our own
        this.form.controls.company.setValue(this.companyFilter.selected ?? this.companies[0]?.id ?? null);
      }
    }
    try {
      const response = await firstValueFrom(this.technician_api_service.get_technicians_list_data(undefined, false));
      this.technicians = (response?.data ?? []).filter(t => t.roles?.id === Roles.TECHNICIAN || t.roles?.id === Roles.OPERATOR_BO);
    } catch {
      this.technicians = [];
    }
  }

  // Technicians of the device's company it can be meant for
  get technicianOptions(): Technician[] {
    const company = this.superadmin ? this.form.controls.company.value : null;
    return this.technicians.filter(t => !company || t.company?.id === company);
  }

  get title(): string {
    return this.mode === 'request' ? 'devices.request' : this.mode === 'edit' ? 'devices.edit' : 'devices.add';
  }

  get typeLocked(): boolean {
    return this.form.controls.usage.value === 'other';
  }

  private applyUsage(): void {
    const type = this.form.controls.device_type;
    if (this.typeLocked) {
      type.setValue('phone', { emitEvent: false });
      type.disable({ emitEvent: false });
    } else if (type.disabled) {
      type.enable({ emitEvent: false });
    }
  }

  private dropTechnicianOfOtherCompany(): void {
    const chosen = this.form.controls.requested_for.value;
    if (chosen && !this.technicianOptions.some(t => t.id === chosen)) {
      this.form.controls.requested_for.setValue(null);
    }
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.saving) {
      return;
    }
    this.saving = true;
    this.errorMessage = '';
    const { company, ...values } = this.form.getRawValue();
    const body: Partial<Device> = { ...values, device_type: values.device_type as Device['device_type'],
      usage: values.usage as Device['usage'], requested_for: values.requested_for || null };
    if (this.superadmin && company) {
      body.company = company;
    }
    const request = this.mode === 'edit' && this.device
      ? this.devices_api_service.update_device(this.device.id, body)
      // A device added by us is trusted and can log in at once; a request waits for our approval (the API decides)
      : this.devices_api_service.create_device(this.mode === 'add' ? { ...body, enabled: true } : body);
    request.subscribe({
      next: () => this.dialogRef.close(this.mode),
      error: (error: string) => {
        this.saving = false;
        // ApiService rethrows only the translated message: the other fields are checked here, so invalid data from the
        // server means the serial number is already used in that company
        const invalid = error === 'validation_error' || error === this.translate.instant('alert.invalid_data');
        this.errorMessage = this.translate.instant(invalid ? 'devices.error_serial_taken' : 'devices.error_save');
      }
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
